'use client';

import { Badge } from '@heidi/design-system/badge';
import * as m from 'motion/react-m';

import type { Keyword } from './types';

// Container dimensions (must match extractKeywords.ts)
const CONTAINER_WIDTH = 320;
const CONTAINER_HEIGHT = 200;
const PADDING = 16;

/**
 * Calculate safe orbital radius based on badge position and dimensions.
 * Ensures the badge never drifts outside the container bounds.
 */
function getSafeOrbitalRadius(
  x: number,
  y: number,
  width: number,
  height: number,
  desiredRadiusX: number,
  desiredRadiusY: number
): { radiusX: number; radiusY: number } {
  // Calculate max safe radius in each direction
  const maxLeft = x - PADDING;
  const maxRight = CONTAINER_WIDTH - PADDING - x - width;
  const maxTop = y - PADDING;
  const maxBottom = CONTAINER_HEIGHT - PADDING - y - height;

  // Clamp to the most restrictive boundary
  const safeRadiusX = Math.min(desiredRadiusX, maxLeft, maxRight);
  const safeRadiusY = Math.min(desiredRadiusY, maxTop, maxBottom);

  // Ensure minimum radius for visible motion (at least 4px)
  return {
    radiusX: Math.max(4, safeRadiusX),
    radiusY: Math.max(4, safeRadiusY),
  };
}

/**
 * Generate a closed-loop orbital path for continuous drift animation.
 * Returns keyframe arrays for x and y that form an elliptical path.
 */
function getOrbitalPath(
  centerX: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
  clockwise: boolean
): { x: number[]; y: number[] } {
  const points = 8; // Octagonal path for smooth motion
  const xPath: number[] = [];
  const yPath: number[] = [];

  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * Math.PI * 2 * (clockwise ? 1 : -1);
    xPath.push(centerX + Math.cos(angle) * radiusX);
    yPath.push(centerY + Math.sin(angle) * radiusY);
  }

  return { x: xPath, y: yPath };
}

interface FloatingKeywordProps {
  keyword: Keyword;
  index: number;
}

export function FloatingKeyword({ keyword, index }: FloatingKeywordProps) {
  // Slow, ambient animation duration (5-7 seconds per orbit)
  const floatDuration = 5 + (index % 3);

  // Stagger entrance for cascade effect (slower for calmer feel)
  const entranceDelay = 0.2 + index * 0.15;

  // Desired orbital dimensions (will be clamped to safe bounds)
  const desiredRadiusX = 10 + (index % 3) * 3; // 10-16px
  const desiredRadiusY = 8 + (index % 2) * 3; // 8-11px

  // Clamp to container bounds
  const { radiusX, radiusY } = getSafeOrbitalRadius(
    keyword.x,
    keyword.y,
    keyword.width,
    keyword.height,
    desiredRadiusX,
    desiredRadiusY
  );

  // Alternate direction per badge for visual variety
  const clockwise = index % 2 === 0;

  // Generate continuous orbital path
  const path = getOrbitalPath(keyword.x, keyword.y, radiusX, radiusY, clockwise);

  return (
    <m.div
      initial={{
        x: keyword.x,
        y: keyword.y,
        opacity: 0,
        scale: 0.9,
      }}
      animate={{
        x: path.x,
        y: path.y,
        opacity: 1,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        scale: 1.05,
        transition: {
          duration: 0.7, // More graceful exit
          ease: 'easeOut',
        },
      }}
      transition={{
        x: {
          duration: floatDuration,
          repeat: Infinity,
          repeatType: 'loop', // Continuous forward motion (no reversal)
          ease: 'linear', // Constant velocity for momentum
        },
        y: {
          duration: floatDuration,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'linear',
        },
        opacity: {
          duration: 0.5, // Gentler appearance
          delay: entranceDelay,
          ease: [0.23, 1, 0.32, 1],
        },
        scale: {
          duration: 0.5, // Gentler appearance
          delay: entranceDelay,
          ease: [0.23, 1, 0.32, 1],
        },
      }}
      className="absolute"
      style={{
        left: 0,
        top: 0,
      }}
    >
      <Badge color="neutral" size="xs">
        {keyword.text}
      </Badge>
    </m.div>
  );
}
